import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

import { AlertCircleIcon, CheckCircleIcon } from "lucide-react";
import { SchemaProperty } from "./SchemaPropertyEditor";

// Parameter interface for path and query parameters
interface Parameter {
  name: string;
  in: "path" | "query";
  type: string;
  required?: boolean;
  description?: string;
  defaultValue?: any;
  enum?: string[];
}

// Import interface should match the one in AgentActionEdit
interface APIActionConfig {
  endpoint?: string;
  method?: string;
  headers?: { [key: string]: string };
  timeout?: number;
  parameters?: Parameter[];
  requestSchema?: { [key: string]: SchemaProperty };
  responseSchemas?: { [statusCode: string]: { [key: string]: SchemaProperty } };
  auth?: {
    type?: string;
    token?: string;
    header?: string;
    key?: string;
  };
}

interface APIImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (config: APIActionConfig, actionName: string, description: string) => void;
}

interface ParsedOperation {
  method: string;
  path: string;
  operationId?: string;
  summary?: string;
  description?: string;
  parameters?: any[];
  requestBody?: any;
  responses?: any;
}

const APIImportModal: React.FC<APIImportModalProps> = ({
  isOpen,
  onClose,
  onImport
}) => {
  const [openApiSpec, setOpenApiSpec] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedOperations, setParsedOperations] = useState<ParsedOperation[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<ParsedOperation | null>(null);

  // Simple YAML parser for OpenAPI specs
  const parseYaml = (yamlStr: string): any => {
    const lines = yamlStr.split("\n");
    const result: any = {};
    const stack: any[] = [{ obj: result, indent: -1 }];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed || trimmed.startsWith("#")) continue;

      const indent = line.length - line.trimStart().length;

      // Handle array items
      if (trimmed.startsWith("- ")) {
        // Pop stack until we find the right parent
        while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
          stack.pop();
        }

        // Find the key that should become an array
        let arrayParent = null;
        let arrayKey = null;
        
        // First, check if we can find an empty object at the current level OR an existing array
        for (let stackIndex = stack.length - 1; stackIndex >= 0; stackIndex--) {
          const stackItem = stack[stackIndex];
          if (stackItem.indent < indent) {
            // Look through the keys of this stack level
            const keys = Object.keys(stackItem.obj);
            for (const key of keys) {
              const value = stackItem.obj[key];
              // Look for empty objects OR existing arrays
              if (value && typeof value === "object") {
                if (Array.isArray(value) || Object.keys(value).length === 0) {
                  arrayParent = stackItem;
                  arrayKey = key;
                  break;
                }
              }
            }
            if (arrayKey) break;
          }
        }
        
        // If we found a key to convert to array (or it's already an array), handle it
        if (arrayKey && arrayParent) {
          if (!Array.isArray(arrayParent.obj[arrayKey])) {
            arrayParent.obj[arrayKey] = [];
          }
        }
        
        const arrayItem = trimmed.substring(2).trim();
        
        // Create new object for this array item
        const newObj: any = {};
        
        // If array item has a colon, it's the first property of the object
        if (arrayItem.includes(":")) {
          const colonIndex = arrayItem.indexOf(":");
          const itemKey = arrayItem.substring(0, colonIndex).trim();
          let itemValue = arrayItem.substring(colonIndex + 1).trim();
          
          // Remove quotes from values
          if ((itemValue.startsWith("\"") && itemValue.endsWith("\"")) || 
              (itemValue.startsWith("'") && itemValue.endsWith("'"))) {
            itemValue = itemValue.slice(1, -1);
          }
          
          if (itemValue) {
            // Handle simple value
            if (itemValue === "true") newObj[itemKey] = true;
            else if (itemValue === "false") newObj[itemKey] = false;
            else if (/^\d+$/.test(itemValue)) newObj[itemKey] = parseInt(itemValue);
            else if (/^\d+\.\d+$/.test(itemValue)) newObj[itemKey] = parseFloat(itemValue);
            else newObj[itemKey] = itemValue;
          } else {
            newObj[itemKey] = {};
          }
        } else if (arrayItem.startsWith("[") && arrayItem.endsWith("]")) {
          // Inline array
          const arrayContent = arrayItem.slice(1, -1).trim();
          const arrayValues = arrayContent ? arrayContent.split(",").map(s => s.trim()) : [];
          if (arrayKey && arrayParent && Array.isArray(arrayParent.obj[arrayKey])) {
            arrayParent.obj[arrayKey].push(...arrayValues);
            continue;
          }
        } else if (arrayItem) {
          // Simple array item - remove quotes
          let cleanItem = arrayItem;
          if ((cleanItem.startsWith("\"") && cleanItem.endsWith("\"")) || 
              (cleanItem.startsWith("'") && cleanItem.endsWith("'"))) {
            cleanItem = cleanItem.slice(1, -1);
          }
          
          if (arrayKey && arrayParent && Array.isArray(arrayParent.obj[arrayKey])) {
            arrayParent.obj[arrayKey].push(cleanItem);
            continue;
          }
        }
        
        // Add the new object to the parent array
        if (arrayKey && arrayParent && Array.isArray(arrayParent.obj[arrayKey])) {
          arrayParent.obj[arrayKey].push(newObj);
        }
        
        // Push this object onto the stack so subsequent properties get added to it
        stack.push({ obj: newObj, indent: indent });
        continue;
      }

      // Handle regular key-value pairs
      const colonIndex = trimmed.indexOf(":");
      if (colonIndex === -1) continue;

      let key = trimmed.substring(0, colonIndex).trim();
      let value = trimmed.substring(colonIndex + 1).trim();

      // Remove quotes from keys and values
      if ((key.startsWith("\"") && key.endsWith("\"")) || 
          (key.startsWith("'") && key.endsWith("'"))) {
        key = key.slice(1, -1);
      }

      if ((value.startsWith("\"") && value.endsWith("\"")) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }

      // Pop stack until we find the right parent
      while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }

      const parent = stack[stack.length - 1];

      if (value) {
        // Handle different value types
        if (value === "true") parent.obj[key] = true;
        else if (value === "false") parent.obj[key] = false;
        else if (/^\d+$/.test(value)) parent.obj[key] = parseInt(value);
        else if (/^\d+\.\d+$/.test(value)) parent.obj[key] = parseFloat(value);
        else if (value.startsWith("[") && value.endsWith("]")) {
          // Simple array parsing
          const arrayContent = value.slice(1, -1).trim();
          const arrayItems = arrayContent ? arrayContent.split(",").map(s => {
            let item = s.trim();
            // Remove quotes from array items
            if ((item.startsWith("\"") && item.endsWith("\"")) || 
                (item.startsWith("'") && item.endsWith("'"))) {
              item = item.slice(1, -1);
            }
            return item;
          }) : [];
          parent.obj[key] = arrayItems;
        } else {
          parent.obj[key] = value;
        }
      } else {
        // Empty value means this is an object or will become an array
        parent.obj[key] = {}; // Initialize as empty object
        stack.push({ obj: parent.obj[key], indent });
      }
    }

    return result;
  };

  // Resolve $ref references in OpenAPI spec
  const resolveReferences = (obj: any, spec: any, visited: Set<string> = new Set()): any => {
    if (!obj || typeof obj !== "object") {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => resolveReferences(item, spec, visited));
    }

    // Check for $ref
    if (obj.$ref && typeof obj.$ref === "string") {
      // Prevent circular references
      if (visited.has(obj.$ref)) {
        console.warn(`Circular reference detected: ${obj.$ref}`);
        return { type: "object", description: `Circular reference to ${obj.$ref}` };
      }

      visited.add(obj.$ref);

      // Parse the reference path
      const refPath = obj.$ref;
      if (refPath.startsWith("#/")) {
        const pathParts = refPath.substring(2).split("/");
        let resolved = spec;
        
        for (const part of pathParts) {
          if (resolved && typeof resolved === "object" && resolved[part]) {
            resolved = resolved[part];
          } else {
            console.warn(`Could not resolve reference: ${refPath}`);
            return { type: "object", description: `Reference not found: ${refPath}` };
          }
        }

        // Recursively resolve the referenced object
        const resolvedObj = resolveReferences(resolved, spec, new Set(visited));
        visited.delete(obj.$ref);
        return resolvedObj;
      }
    }

    // Handle allOf, oneOf, anyOf
    if (obj.allOf && Array.isArray(obj.allOf)) {
      const mergedSchema: any = { properties: {} };
      
      for (const subSchema of obj.allOf) {
        const resolvedSubSchema = resolveReferences(subSchema, spec, visited);
        
        // Merge properties deeply
        if (resolvedSubSchema.properties) {
          if (!mergedSchema.properties) mergedSchema.properties = {};
          mergedSchema.properties = { ...mergedSchema.properties, ...resolvedSubSchema.properties };
        }
        
        // Merge other properties, with special handling for type
        Object.keys(resolvedSubSchema).forEach(key => {
          if (key !== "properties" && key !== "allOf") {
            if (key === "type") {
              // Only set type if not already set, or if explicitly set to "object" 
              if (!mergedSchema.type || resolvedSubSchema.type === "object") {
                mergedSchema[key] = resolvedSubSchema[key];
              }
            } else {
              mergedSchema[key] = resolvedSubSchema[key];
            }
          }
        });
        
        // Merge required arrays
        if (resolvedSubSchema.required && Array.isArray(resolvedSubSchema.required)) {
          if (!mergedSchema.required) mergedSchema.required = [];
          mergedSchema.required = Array.from(new Set([...mergedSchema.required, ...resolvedSubSchema.required]));
        }
      }
      
      // Merge any additional properties from the current object
      Object.keys(obj).forEach(key => {
        if (key !== "allOf") {
          if (key === "properties") {
            if (!mergedSchema.properties) mergedSchema.properties = {};
            mergedSchema.properties = { ...mergedSchema.properties, ...obj[key] };
          } else if (key === "required" && Array.isArray(obj[key])) {
            if (!mergedSchema.required) mergedSchema.required = [];
            mergedSchema.required = Array.from(new Set([...mergedSchema.required, ...obj[key]]));
          } else if (key === "type") {
            // Only set type if not already set, or if explicitly set to "object"
            if (!mergedSchema.type || obj.type === "object") {
              mergedSchema[key] = obj[key];
            }
          } else {
            mergedSchema[key] = obj[key];
          }
        }
      });
      
      // Ensure we have a type if none was set
      if (!mergedSchema.type && mergedSchema.properties && Object.keys(mergedSchema.properties).length > 0) {
        mergedSchema.type = "object";
      }
      
      return mergedSchema;
    }

    // Recursively resolve nested objects
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = resolveReferences(value, spec, visited);
    }

    return result;
  };

  // Convert OpenAPI schema to our SchemaProperty format
  const convertOpenAPISchemaToSchemaProperty = (schema: any, name: string = "", description: string = "", fullSpec?: any): SchemaProperty => {
    if (!schema || typeof schema !== "object") {
      return {
        name: name || "property",
        type: "string",
        description: description || ""
      };
    }

    // Resolve references if we have the full spec
    let resolvedSchema = schema;
    if (fullSpec && (schema.$ref || schema.allOf)) {
      resolvedSchema = resolveReferences(schema, fullSpec);
    }

    const schemaProperty: SchemaProperty = {
      name: name || "property",
      type: resolvedSchema.type || "string",
      description: description || resolvedSchema.description || "",
      required: false
    };

    // Handle format
    if (resolvedSchema.format) {
      schemaProperty.format = resolvedSchema.format;
    }

    // Handle enum
    if (resolvedSchema.enum && Array.isArray(resolvedSchema.enum)) {
      schemaProperty.enum = resolvedSchema.enum;
    }

    // Handle object properties
    if (resolvedSchema.type === "object" && resolvedSchema.properties && typeof resolvedSchema.properties === "object" && resolvedSchema.properties !== null) {
      schemaProperty.properties = {};
      Object.entries(resolvedSchema.properties).forEach(([propName, propSchema]: [string, any]) => {
        const isRequired = resolvedSchema.required && Array.isArray(resolvedSchema.required) && resolvedSchema.required.includes(propName);
        const convertedProp = convertOpenAPISchemaToSchemaProperty(propSchema, propName, propSchema?.description, fullSpec);
        convertedProp.required = isRequired;
        schemaProperty.properties![propName] = convertedProp;
      });
    }

    // Handle array items
    if (resolvedSchema.type === "array" && resolvedSchema.items) {
      schemaProperty.items = convertOpenAPISchemaToSchemaProperty(resolvedSchema.items, "item", resolvedSchema.items?.description, fullSpec);
    }

    return schemaProperty;
  };

  // Convert OpenAPI schema to our property format for request/response schemas
  const convertSchemaToProperties = (schema: any, fullSpec?: any): { [key: string]: SchemaProperty } => {
    if (!schema) {
      return {};
    }


    // Resolve references if we have the full spec
    let resolvedSchema = schema;
    if (fullSpec && (schema.$ref || schema.allOf)) {
      resolvedSchema = resolveReferences(schema, fullSpec);
    }

    // If we have properties, process them normally
    if (resolvedSchema.properties && typeof resolvedSchema.properties === "object") {
      const properties: { [key: string]: SchemaProperty } = {};
      Object.entries(resolvedSchema.properties).forEach(([propName, propSchema]: [string, any]) => {
        const isRequired = resolvedSchema.required && Array.isArray(resolvedSchema.required) && resolvedSchema.required.includes(propName);
        const convertedProp = convertOpenAPISchemaToSchemaProperty(propSchema, propName, propSchema?.description, fullSpec);
        convertedProp.required = isRequired;
        properties[propName] = convertedProp;
      });
      return properties;
    }

    // If the resolved schema doesn't have properties but has other schema info,
    // create a single property representing the entire response
    if (resolvedSchema.type || resolvedSchema.$ref) {
      return {
        "value": convertOpenAPISchemaToSchemaProperty(resolvedSchema, "value", resolvedSchema.description || "Response value", fullSpec)
      };
    }

    return {};
  };

  // Fix common YAML parsing issues like nested parameters
  const fixYamlParsingIssues = (obj: any): any => {
    if (!obj || typeof obj !== "object") return obj;

    if (Array.isArray(obj)) {
      return obj.map(fixYamlParsingIssues);
    }

    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      let fixedValue = value;

      // Fix nested parameters issue: { parameters: { parameters: [...] } } -> { parameters: [...] }
      if (key === "parameters" && value && typeof value === "object" && !Array.isArray(value)) {
        const nestedValue = (value as any).parameters;
        if (Array.isArray(nestedValue)) {
          fixedValue = nestedValue;
        } else {
          // Convert object with numeric keys to array
          const paramKeys = Object.keys(value);
          if (paramKeys.length > 0 && paramKeys.every(k => /^\d+$/.test(k))) {
            const paramArray = [];
            const valueObj = value as any;
            for (let i = 0; i < paramKeys.length; i++) {
              if (valueObj[i.toString()]) {
                paramArray.push(valueObj[i.toString()]);
              }
            }
            fixedValue = paramArray;
          } else if (paramKeys.length > 0) {
            // Check if this looks like a single parameter object that should be in an array
            const firstValue = (value as any)[paramKeys[0]];
            if (firstValue && typeof firstValue === "object" && 
                (firstValue.name || firstValue.in || firstValue.schema)) {
              // This looks like parameter objects, convert to array
              fixedValue = Object.values(value);
            }
          }
        }
      }

      result[key] = fixYamlParsingIssues(fixedValue);
    }

    return result;
  };

  // Enhanced YAML parser specifically for OpenAPI specs
  const parseOpenAPIYaml = (yamlStr: string): any => {
    
    // Try our custom parser first
    let parsed = parseYaml(yamlStr);
    
    // Apply fixes
    parsed = fixYamlParsingIssues(parsed);
    
    // Additional fix for parameters array that might not be parsed correctly
    const fixParametersArray = (obj: any): any => {
      if (!obj || typeof obj !== "object") return obj;
      
      if (Array.isArray(obj)) {
        return obj.map(fixParametersArray);
      }
      
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        if (key === "parameters" && value && typeof value === "object" && !Array.isArray(value)) {
          // If parameters is an object but should be an array, try to extract array elements
          const paramKeys = Object.keys(value);
          
          if (paramKeys.length > 0 && paramKeys.every(k => /^\d+$/.test(k))) {
            // Looks like array indices as keys, convert to array
            const paramArray = [];
            const valueObj = value as any;
            for (let i = 0; i < paramKeys.length; i++) {
              if (valueObj[i.toString()]) {
                paramArray.push(valueObj[i.toString()]);
              }
            }
            result[key] = paramArray;
          } else if (paramKeys.length > 0) {
            // Check if any of the values look like parameter objects
            const firstValue = (value as any)[paramKeys[0]];
            if (firstValue && typeof firstValue === "object" && 
                (firstValue.name || firstValue.in || firstValue.schema || firstValue.type)) {
              result[key] = Object.values(value);
            } else {
              result[key] = fixParametersArray(value);
            }
          } else {
            // Empty object, keep as is
            result[key] = value;
          }
        } else {
          result[key] = fixParametersArray(value);
        }
      }
      return result;
    };
    
    parsed = fixParametersArray(parsed);
    
    return parsed;
  };

  const parseOpenAPISpec = () => {
    setIsLoading(true);
    setError(null);
    
    try {
      let spec: any;
      
      // Try to parse as YAML first, then JSON
      try {
        if (openApiSpec.trim().startsWith("{")) {
          spec = JSON.parse(openApiSpec);
        } else {
          // Use the enhanced YAML parser with debugging
          spec = parseOpenAPIYaml(openApiSpec);
        }
      } catch (yamlError) {
        // If YAML parsing fails, try JSON
        try {
          spec = JSON.parse(openApiSpec);
        } catch (jsonError) {
          console.error("Parsing errors - YAML:", yamlError, "JSON:", jsonError);
          throw new Error(`Failed to parse specification as YAML or JSON.

YAML Error: ${yamlError instanceof Error ? yamlError.message : String(yamlError)}
JSON Error: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}

Suggestions:
• Verify your YAML indentation is correct
• Ensure all strings are properly quoted
• Try converting to JSON format for better compatibility`);
        }
      }

      const operations: ParsedOperation[] = [];

      // Detect if this is a full spec, paths object, or single operation
      if (spec.paths) {
        // Full OpenAPI spec
        Object.entries(spec.paths).forEach(([path, pathItem]: [string, any]) => {
          Object.entries(pathItem).forEach(([method, operation]: [string, any]) => {
            if (["get", "post", "put", "patch", "delete", "head", "options", "trace"].includes(method.toLowerCase())) {
              operations.push({
                method: method.toLowerCase(),
                path,
                operationId: operation.operationId,
                summary: operation.summary,
                description: operation.description,
                parameters: operation.parameters,
                requestBody: operation.requestBody,
                responses: operation.responses
              });
            }
          });
        });
      } else if (Object.keys(spec).some(key => key.startsWith("/"))) {
        // Paths object
        Object.entries(spec).forEach(([path, pathItem]: [string, any]) => {
          Object.entries(pathItem).forEach(([method, operation]: [string, any]) => {
            if (["get", "post", "put", "patch", "delete", "head", "options", "trace"].includes(method.toLowerCase())) {
              operations.push({
                method: method.toLowerCase(),
                path,
                operationId: operation.operationId,
                summary: operation.summary,
                description: operation.description,
                parameters: operation.parameters,
                requestBody: operation.requestBody,
                responses: operation.responses
              });
            }
          });
        });
      } else {
        // Assume single operation
        const pathKeys = Object.keys(spec);
        const operationMethods = ["get", "post", "put", "patch", "delete", "head", "options", "trace"];
        
        for (const pathKey of pathKeys) {
          if (pathKey.startsWith("/")) {
            const pathItem = spec[pathKey];
            Object.entries(pathItem).forEach(([method, operation]: [string, any]) => {
              if (operationMethods.includes(method.toLowerCase())) {
                operations.push({
                  method: method.toLowerCase(),
                  path: pathKey,
                  operationId: operation.operationId,
                  summary: operation.summary,
                  description: operation.description,
                  parameters: operation.parameters,
                  requestBody: operation.requestBody,
                  responses: operation.responses
                });
              }
            });
            break;
          }
        }
      }

      if (operations.length === 0) {
        throw new Error("No valid operations found in the OpenAPI specification. Please ensure your spec contains at least one operation with a valid HTTP method (GET, POST, etc.).");
      }

      setParsedOperations(operations);
      setSelectedOperation(operations[0]); // Auto-select first operation
    } catch (err) {
      console.error("Parse error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = () => {
    if (!selectedOperation) return;

    setIsLoading(true);
    try {
      // Get the full spec for reference resolution
      let fullSpec: any = null;
      try {
        if (openApiSpec.trim().startsWith("{")) {
          fullSpec = JSON.parse(openApiSpec);
        } else {
          fullSpec = parseOpenAPIYaml(openApiSpec);
        }
      } catch (err) {
        console.warn("Could not parse full spec for reference resolution:", err);
      }

      const config: APIActionConfig = {
        endpoint: selectedOperation.path,
        method: selectedOperation.method.toUpperCase(),
        headers: { "Content-Type": "application/json" },
        timeout: 30,
        parameters: [],
        requestSchema: {},
        responseSchemas: {},
        auth: {
          type: "none",
          token: "",
          header: "Authorization",
          key: "",
        }
      };

      // Convert parameters
      if (selectedOperation.parameters && Array.isArray(selectedOperation.parameters)) {
        config.parameters = selectedOperation.parameters
          .filter(param => param.in === "path" || param.in === "query")
          .map(param => ({
            name: param.name,
            in: param.in as "path" | "query",
            type: param.schema?.type || "string",
            required: param.required || false,
            description: param.description,
            defaultValue: param.schema?.default,
            enum: param.schema?.enum
          }));
      }

      // Convert request body
      if (selectedOperation.requestBody && 
          selectedOperation.requestBody.content && 
          selectedOperation.requestBody.content["application/json"] &&
          selectedOperation.requestBody.content["application/json"].schema) {
        const schema = selectedOperation.requestBody.content["application/json"].schema;
        config.requestSchema = convertSchemaToProperties(schema, fullSpec);
      }

      // Convert response schemas
      if (selectedOperation.responses && typeof selectedOperation.responses === "object") {
        Object.entries(selectedOperation.responses).forEach(([statusCode, response]: [string, any]) => {
          if (response.content && 
              response.content["application/json"] && 
              response.content["application/json"].schema) {
            const schema = response.content["application/json"].schema;
            const convertedSchema = convertSchemaToProperties(schema, fullSpec);
            config.responseSchemas![statusCode] = convertedSchema;
          }
        });
      }

      // Use operationId as action name, fallback to summary or generated name
      const actionName = selectedOperation.operationId || 
                        selectedOperation.summary || 
                        `${selectedOperation.method}_${selectedOperation.path.replace(/[^\w]/g, "_")}`;
      
      const description = selectedOperation.description || 
                         selectedOperation.summary || 
                         `${selectedOperation.method} ${selectedOperation.path}`;

      onImport(config, actionName, description);
      onClose();
    } catch (err) {
      setError(`Failed to import operation: ${err instanceof Error ? err.message : "Unknown error"}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpenApiSpec("");
    setError(null);
    setParsedOperations([]);
    setSelectedOperation(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Import from Spec</DialogTitle>
          <DialogDescription>
            Import API configuration from an OpenAPI/Swagger spec.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-6">
          {/* Step 1: Paste OpenAPI Spec */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">Step 1: Paste Spec</Label>
              <Button
                onClick={parseOpenAPISpec}
                disabled={!openApiSpec.trim()}
                size="sm"
                variant="outline"
              >
                Parse Spec
              </Button>
            </div>
            <Textarea
              value={openApiSpec}
              onChange={(e) => setOpenApiSpec(e.target.value)}
              placeholder={`Paste your spec here (JSON or YAML)...

Examples:

# YAML format (supports partial specs):
/verification/health-check:
  get:
    tags: [verification]
    summary: System health check
    parameters:
      - name: include_screenshot
        in: query
        schema:
          type: boolean
          default: false
    responses:
      '200':
        description: Health check completed

# JSON format:
{
  "openapi": "3.0.0",
  "paths": {
    "/users": {
      "get": {
        "summary": "List users",
        "parameters": [
          {
            "name": "limit",
            "in": "query", 
            "schema": { "type": "integer" }
          }
        ],
        "responses": {
          "200": { "description": "Success" }
        }
      }
    }
  }
}`}
              className="min-h-[200px] font-mono text-sm"
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-200">Parsing Error</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Step 2: Select Operation */}
          {parsedOperations.length > 0 && (
            <div className="space-y-4">
              <Label className="text-base font-medium">Step 2: Select Operation</Label>
              <div className="grid gap-3">
                {parsedOperations.map((operation, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedOperation === operation
                        ? "border-maitai-green bg-maitai-green/5 dark:bg-maitai-green/10"
                        : "border-border hover:border-maitai-green/50"
                    }`}
                    onClick={() => setSelectedOperation(operation)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                            operation.method === "GET" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300" :
                            operation.method === "POST" ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                            operation.method === "PUT" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300" :
                            operation.method === "PATCH" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300" :
                            operation.method === "DELETE" ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                            "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                          }`}>
                            {operation.method}
                          </span>
                          <span className="font-mono text-sm text-foreground">{operation.path}</span>
                        </div>
                        <div className="space-y-1">
                          {operation.summary && (
                            <p className="font-medium text-foreground">{operation.summary}</p>
                          )}
                          {operation.description && (
                            <p className="text-sm text-muted-foreground">{operation.description}</p>
                          )}
                          {operation.operationId && (
                            <p className="text-xs text-muted-foreground">
                              Operation ID: <span className="font-mono">{operation.operationId}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      {selectedOperation === operation && (
                        <CheckCircleIcon className="w-5 h-5 text-maitai-green flex-shrink-0" />
                      )}
                    </div>
                    
                    {/* Operation Details */}
                    <div className="mt-3 pt-3 border-t border-border space-y-2">
                      {operation.parameters && operation.parameters.length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Parameters:</p>
                          <div className="flex flex-wrap gap-1">
                            {operation.parameters.map((param: any, paramIndex: number) => (
                              <span
                                key={paramIndex}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs"
                              >
                                <span className="font-mono">{param.name}</span>
                                <span className="text-muted-foreground">({param.in})</span>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {operation.requestBody && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">Has request body</p>
                        </div>
                      )}
                      
                      {operation.responses && Object.keys(operation.responses).length > 0 && (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Responses:</p>
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(operation.responses).map((statusCode) => (
                              <span
                                key={statusCode}
                                className={`px-2 py-0.5 rounded text-xs font-mono ${
                                  statusCode.startsWith("2") ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                                  statusCode.startsWith("4") ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300" :
                                  statusCode.startsWith("5") ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                                  "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                                }`}
                              >
                                {statusCode}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}


        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={!selectedOperation || isLoading}
          >
            {isLoading ? "Importing..." : "Import Configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default APIImportModal; 